/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
    BehaviorSubject,
    Observable
} from 'rxjs';
import {
    delay,
    distinctUntilChanged
} from 'rxjs/operators';

/**
 * <p>Manages the loading spinner local context.
 * <p><i>This class is part of {@link SpinnerService} and is meant to be final, do <b>not</b> extend.</i>
 * @internal
 */
export class SpinnerLocalContext {
    /**
     * Keeps track of how many usages each loading spinner has.
     * @private
     */
    private map = new Map<unknown, number>();

    /**
     * Keeps track of notifiers for each loading spinner.
     * @private
     */
    private subjects = new Map<unknown, BehaviorSubject<boolean>>();

    /**
     * Returns the observable responsible for the wanted loading spinner.
     * @param key The wanted loading spinner reference.
     * @return The loading spinner observable.
     */
    onChange<T>(key: T): Observable<boolean> {
        this.createSubject(key);
        return this.subjects.get(key)
                .pipe(distinctUntilChanged(), delay(0));
    }

    /**
     * Adds a loading spinner.
     * @param key The wanted loading spinner reference.
     */
    add<T>(key: T): void {
        this.map.set(key, this.usageCount(key) + 1);

        this.emit(key);
    }

    /**
     * Removes a loading spinner.
     * @param key The wanted loading spinner reference.
     */
    remove<T>(key: T): void {
        if (this.isInUse(key)) {
            this.map.set(key, this.usageCount(key) - 1);
        }

        this.emit(key);
    }

    /**
     * Emits true if the loading spinner is being used at least once, false otherwise.
     * @param key The wanted loading spinner reference.
     * @private
     */
    private emit<T>(key: T): void {
        this.createSubject(key);
        this.subjects.get(key)
                .next(this.isInUse(key));
    }

    /**
     * How many usages a loading spinner reference has.
     * @param key The wanted loading spinner reference.
     * @returns The amount of usages a loading spinner reference has.
     * @private
     */
    private usageCount<T>(key: T): number {
        return this.map.get(key) ?? 0;
    }

    /**
     * Check if a loading spinner reference is in use.
     * @param key The wanted loading spinner reference.
     * @returns True if the loading spinner reference is in use, false otherwise.
     * @private
     */
    private isInUse<T>(key: T): boolean {
        return this.usageCount(key) > 0;
    }

    /**
     * Creates, if necessary, a subject for a loading spinner reference.
     * @param key The wanted loading spinner reference.
     * @private
     */
    private createSubject<T>(key: T): void {
        if (!this.subjects.has(key)) {
            this.subjects.set(key, new BehaviorSubject<boolean>(this.isInUse(key)));
        }
    }
}
